import { Alert, Box, MenuItem, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { FormAutocomplete, FormDialog, FormTextField, ValueBadge } from '../../../design-system';
import type { ImpersonationPurposeCode, ImpersonationTarget } from './model';
import type { ImpersonationPort } from './port';

const purposes: ReadonlyArray<{ value: ImpersonationPurposeCode; label: string }> = [
  { value: 'SUPPORT', label: '사용자 지원' },
  { value: 'ACCESS_VERIFICATION', label: '접근 권한 확인' },
  { value: 'TROUBLESHOOTING', label: '문제 해결' },
];

export type ImpersonationDialogProps = {
  open: boolean;
  port: ImpersonationPort;
  onClose: () => void;
  onStarted: () => void;
  initialIdentityId?: string;
  initialSearch?: string;
};

export function ImpersonationDialog({ open, port, onClose, onStarted, initialIdentityId, initialSearch }: ImpersonationDialogProps) {
  const [search, setSearch] = useState('');
  const pendingInitialSelection = useRef<string | undefined>(undefined);
  const [targets, setTargets] = useState<ImpersonationTarget[]>([]);
  const [selected, setSelected] = useState<ImpersonationTarget | null>(null);
  const [purposeCode, setPurposeCode] = useState<ImpersonationPurposeCode | ''>('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!open) return;
    setSearch(initialSearch ?? '');
    pendingInitialSelection.current = initialIdentityId;
    setTargets([]);
    setSelected(null);
    setPurposeCode('');
    setSubmitting(false);
    setError(undefined);
  }, [open, initialIdentityId, initialSearch]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setTargets([]);
    const timer = window.setTimeout(() => {
      setError(undefined);
      void port.listTargets(search).then(
        (response) => {
          if (!active) return;
          setTargets(response.data);
          if (pendingInitialSelection.current) {
            const target = response.data.find((item) => item.identityId === pendingInitialSelection.current);
            if (target) { setSelected(target); setSearch(target.displayName); }
            pendingInitialSelection.current = undefined;
          }
        },
        (reason: unknown) => {
          if (!active) return;
          setTargets([]);
          setError(reason instanceof Error ? reason.message : '사용자 목록을 불러오지 못했습니다.');
        },
      ).finally(() => { if (active) setLoading(false); });
    }, search.trim() ? 250 : 0);
    return () => { active = false; window.clearTimeout(timer); };
  }, [open, port, search]);

  const start = async () => {
    if (!selected || !purposeCode) return;
    setSubmitting(true);
    setError(undefined);
    try {
      await port.start({ targetIdentityId: selected.identityId, purposeCode });
      onStarted();
      onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '다른 사용자로 보기를 시작하지 못했습니다.');
      setSubmitting(false);
    }
  };

  return <FormDialog
    open={open}
    onClose={onClose}
    onSubmit={start}
    title="다른 사용자로 보기"
    cancelLabel="취소"
    submitLabel={selected ? `${selected.displayName}님으로 보기` : '사용자 선택'}
    busy={submitting}
    busyLabel="전환 중"
    submitDisabled={!selected || !purposeCode}
  >
    {error && <Alert severity="error">{error}</Alert>}
    <Alert severity="warning">
      선택한 사용자의 권한으로 조회합니다. 이 세션에서는 변경 작업을 수행할 수 없으며, 전환과 조회 내역은 감사 기록에 남습니다.
    </Alert>
    <FormAutocomplete
      value={selected}
      inputValue={search}
      options={targets}
      loading={loading}
      disabled={submitting}
      filterOptions={(options) => options}
      getOptionLabel={(option) => option.displayName}
      getOptionKey={(option) => option.identityId}
      isOptionEqualToValue={(option, value) => option.identityId === value.identityId}
      onChange={(_, value) => { setSelected(value); setSearch(value?.displayName ?? ''); }}
      onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') { pendingInitialSelection.current = undefined; setSelected(null); setSearch(value); } }}
      loadingText="사용자를 검색하는 중입니다."
      noOptionsText={search.trim() ? '검색 결과가 없습니다.' : '전환할 수 있는 사용자가 없습니다.'}
      input={{ required: true, label: '사용자 검색', helperText: '이름, 사번 또는 조직으로 검색' }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return <Box component="li" key={key} {...optionProps}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 720 }}>{option.displayName} · {option.workerNumber}</Typography>
            <Typography variant="caption" color="text.secondary">{option.organization || '소속 미등록'}</Typography>
          </Box>
        </Box>;
      }}
    />
    <FormTextField
      select
      required
      disabled={submitting}
      label="사용 목적"
      value={purposeCode}
      onChange={(event) => setPurposeCode(event.target.value as ImpersonationPurposeCode)}
      help="선택한 목적은 감사 기록에 저장됩니다."
    >
      <MenuItem value="" disabled>목적 선택</MenuItem>
      {purposes.map((purpose) => <MenuItem key={purpose.value} value={purpose.value}><ValueBadge label={purpose.label} /></MenuItem>)}
    </FormTextField>
    {selected && <Alert severity="info">{selected.displayName} · {selected.workerNumber} · {selected.organization || '소속 미등록'}</Alert>}
  </FormDialog>;
}
